import { CircleOff, CirclePlay, FileText, Info, Pencil, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { PATHS } from '@/app/router/routes'
import { Permission } from '@/features/roles/consts/permissions'
import { DetailFieldRow } from '@/shared/components/detail-rows/detail-field-row'
import { UserChip } from '@/shared/components/detail-rows/user-chip'
import { SidePanelActions } from '@/shared/components/side-panel/side-panel-actions'
import {
  SidePanelSection,
  SidePanelSectionBody
} from '@/shared/components/side-panel/side-panel-blocks'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { useSidePanelStore } from '@/shared/store/side-panel.store'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/shared/ui/accordion'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/shared/ui/alert-dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs'
import { formatHumanDateTime } from '@/shared/utils/format-date'
import { useDeleteStateMutation, useUpdateStateMutation } from '../hooks/use-state-mutations'
import { LABEL_TYPE_NOTIFICATION } from '../lib/consts'
import type { StateAdminRow } from '../models/state-admin'
import {
  formatStateSla,
  StateActiveBadge,
  StateColorDot,
  StateTypeBadge
} from './states-admin-columns'

type StateDetailPanelProps = {
  state: StateAdminRow
}

/**
 * Panel de detalle del estado: resumen + acciones + acordeones (notificación,
 * requisitos documentales, actividad). La EDICIÓN vive en la página completa
 * (create-update-state) porque incluye el editor de requisitos del portal —
 * espejo del atajo a plantillas de proyectos. Acciones que el backend rechaza
 * de plano se OCULTAN: Eliminar y Editar para filas de sistema (blocked; su
 * tipo no es editable y el borrado responde 400).
 */
export function StateDetailPanel({ state }: StateDetailPanelProps) {
  const [confirmDelete, setConfirmDelete] = useState(false)
  const closePanel = useSidePanelStore((s) => s.closePanel)
  const navigate = useNavigate()
  const updateMutation = useUpdateStateMutation()
  const deleteMutation = useDeleteStateMutation()
  const { has } = usePermissions()

  // Las filas de sistema (blocked) no se editan ni se eliminan.
  const isSystemRow = state.blocked

  const toggleActive = () => {
    updateMutation.mutate({ id: state._id, payload: { active: !state.active } })
  }

  // Docs sembrados antes de existir estos campos no los traen (Mongo no
  // aplica defaults retroactivos) — siempre con fallback.
  const requirements = state.attachmentRequirements ?? []
  const customEmails = state.emailsNotification ?? []
  const notificationLabels = (state.notification ?? []).map(
    (recipient) => LABEL_TYPE_NOTIFICATION[recipient] ?? recipient
  )

  return (
    <div className="flex flex-col">
      <SidePanelSection>
        <SidePanelSectionBody className="gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <StateColorDot color={state.color} />
            <h3 className="truncate text-base font-semibold leading-tight">{state.name}</h3>
          </div>
          {state.description && (
            <p className="text-sm text-muted-foreground">{state.description}</p>
          )}
          <div className="flex flex-col gap-2">
            <DetailFieldRow label="Estado" value={<StateActiveBadge active={state.active} />} />
            <DetailFieldRow label="Tipo" value={<StateTypeBadge type={state.type} />} />
            <DetailFieldRow label="SLA" value={formatStateSla(state.time, state.interval)} />
            <DetailFieldRow label="Visible" value={state.visible ? 'Sí' : 'No'} />
            <DetailFieldRow label="Envía a SAP" value={state.sendSAP ? 'Sí' : 'No'} />
          </div>
        </SidePanelSectionBody>
      </SidePanelSection>

      <SidePanelSection>
        <SidePanelActions
          maxInline={2}
          actions={[
            ...(has(Permission.UpdateStates) && !isSystemRow
              ? [
                  {
                    label: 'Editar estado',
                    icon: <Pencil className="h-4 w-4" />,
                    variant: 'default' as const,
                    primary: true,
                    onClick: () => {
                      closePanel()
                      void navigate(`${PATHS.admin.business.states}/${state._id}`)
                    }
                  }
                ]
              : []),
            ...(has(Permission.UpdateStates)
              ? [
                  {
                    label: state.active ? 'Desactivar estado' : 'Activar estado',
                    icon: state.active ? (
                      <CircleOff className="h-4 w-4" />
                    ) : (
                      <CirclePlay className="h-4 w-4" />
                    ),
                    variant: 'outline' as const,
                    disabled: updateMutation.isPending,
                    onClick: toggleActive
                  }
                ]
              : []),
            ...(has(Permission.DeleteStates) && !isSystemRow
              ? [
                  {
                    label: 'Eliminar estado',
                    icon: <Trash2 className="h-4 w-4" />,
                    variant: 'destructive' as const,
                    disabled: deleteMutation.isPending,
                    onClick: () => {
                      setConfirmDelete(true)
                    }
                  }
                ]
              : [])
          ]}
        />
      </SidePanelSection>

      <Tabs defaultValue="more-info">
        <TabsList className="w-full justify-normal rounded-none">
          <TabsTrigger className="flex gap-1" value="more-info">
            <Info className="size-4" /> Más información
          </TabsTrigger>
        </TabsList>
        <TabsContent value="more-info">
          <Accordion type="multiple" defaultValue={['notification', 'activity']}>
            <AccordionItem value="notification">
              <AccordionTrigger className="px-3 border-b border-transparent data-[state=open]:border-border">
                Notificaciones
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-2 pt-2 pl-3 pr-2">
                  <DetailFieldRow
                    label="Destinatarios"
                    value={notificationLabels.length ? notificationLabels.join(', ') : '—'}
                  />
                  <DetailFieldRow
                    label="Correos personalizados"
                    value={customEmails.length ? customEmails.join(', ') : '—'}
                  />
                </div>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="requirements">
              <AccordionTrigger className="px-3 border-b border-transparent data-[state=open]:border-border">
                <span className="flex items-center gap-2">
                  <FileText className="size-4" />
                  Requisitos documentales ({requirements.length})
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-2 pt-2 pl-3 pr-2">
                  {requirements.length === 0 && (
                    <p className="text-sm text-muted-foreground">
                      Este estado no exige documentos.
                    </p>
                  )}
                  {requirements.map((req) => (
                    <DetailFieldRow
                      key={req.id}
                      label={req.name}
                      value={req.isRequired ? 'Obligatorio' : 'Opcional'}
                    />
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="activity">
              <AccordionTrigger className="px-3 border-b border-transparent data-[state=open]:border-border">
                Actividad
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-2 pt-2 pl-3 pr-2">
                  <DetailFieldRow label="Creado" value={formatHumanDateTime(state.createdAt)} />
                  <DetailFieldRow
                    label="Creado por"
                    value={<UserChip name={state.createdBy?.fullname} />}
                  />
                  <DetailFieldRow
                    label="Actualizado"
                    value={formatHumanDateTime(state.updatedAt)}
                  />
                  <DetailFieldRow
                    label="Actualizado por"
                    value={<UserChip name={state.updatedBy?.fullname} />}
                  />
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </TabsContent>
      </Tabs>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar estado</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará “{state.name}” y se quitará de todos los flujos de trabajo que lo
              incluyen. Los clientes y negociaciones que están en este estado conservan su
              referencia histórica.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                deleteMutation.mutate(state._id, {
                  onSuccess: () => {
                    closePanel()
                  }
                })
              }}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
